import React, { useState, useCallback, useEffect } from 'react';
import { useStoreComparison } from '../../../hooks/useStoreComparison';
import { OrderComparison } from '../../../types/comparative';
import ComparisonMetrics from './ComparisonMetrics';
import ComparisonResults from './ComparisonResults';
import ComparisonActions from './ComparisonActions';
import LoadingSpinner from '../../LoadingSpinner';
import Pagination from '../../ui/Pagination';
import toast from 'react-hot-toast';
import axiosInstance from '../../../api/axiosInstance';

const ComparativeTable: React.FC = () => {
  const [selectedStore, setSelectedStore] = useState('');
  const [dateRange, setDateRange] = useState<{ start: string; end: string }>({
    start: '',
    end: ''
  });
  const [isExecuting, setIsExecuting] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [searchTerm, setSearchTerm] = useState('');

  const {
    isLoading,
    error,
    comparisonData,
    metrics,
    pagination,
    compareOrders,
    clearData
  } = useStoreComparison();

  const handleStoreChange = useCallback((store: string) => {
    setSelectedStore(store);
    setCurrentPage(1); // Resetear a la primera página
    // Limpiar datos anteriores al cambiar tienda
    if (comparisonData.length > 0) {
      clearData();
    }
  }, [comparisonData.length, clearData]);

  const handleExecuteComparison = useCallback(async (page?: number) => {
    if (!selectedStore) {
      toast.error('Por favor seleccione una tienda para comparar');
      return;
    }

    setIsExecuting(true);
    try {
      await compareOrders({
        store: selectedStore,
        page: page || currentPage,
        per_page: pageSize,
        dateRange: dateRange.start && dateRange.end ? dateRange : undefined,
        search: searchTerm
      });
      
      toast.success(`Comparación con ${selectedStore} completada exitosamente`, {
        icon: '✅'
      });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error desconocido';
      toast.error(`Error en la comparación: ${errorMessage}`, {
        icon: '❌'
      });
    } finally {
      setIsExecuting(false);
    }
  }, [selectedStore, currentPage, pageSize, dateRange, searchTerm, compareOrders]);

  const handleRefresh = useCallback(() => {
    if (selectedStore) {
      handleExecuteComparison();
    }
  }, [selectedStore, handleExecuteComparison]);

  const handleExecuteComparisonClick = useCallback(() => {
    handleExecuteComparison();
  }, [handleExecuteComparison]);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
    if (selectedStore) {
      handleExecuteComparison(page);
    }
  }, [selectedStore, handleExecuteComparison]);

  const handlePageSizeChange = useCallback((size: number) => {
    setPageSize(size);
    setCurrentPage(1); // Resetear a la primera página
    if (selectedStore) {
      handleExecuteComparison(1);
    }
  }, [selectedStore, handleExecuteComparison]);

  const handleSearchChange = useCallback((term: string) => {
    setSearchTerm(term);
    setCurrentPage(1); // Resetear a la primera página
    // La búsqueda se manejará en el backend
  }, []);

  // Efecto para ejecutar búsqueda con debounce
  useEffect(() => {
    if (!selectedStore) return;
    
    const timeoutId = setTimeout(() => {
      if (searchTerm !== '') {
        handleExecuteComparison();
      }
    }, 500); // 500ms de debounce

    return () => clearTimeout(timeoutId);
  }, [searchTerm, selectedStore, handleExecuteComparison]);

  const handleViewDetails = useCallback((comparison: OrderComparison) => {
    // Implementar modal de detalles
    console.log('Ver detalles de comparación:', comparison);
    toast('Funcionalidad de detalles en desarrollo', {
      icon: '🔧'
    });
  }, []);

  const handleSyncOrder = useCallback(async (orderId: string) => {
    if (selectedStore !== 'OFIMA') {
      toast.error('La sincronización individual solo está disponible para OFIMA');
      return;
    }

    try {
      const response = await axiosInstance.post('/ofima/send-to-ofima', {
        orderIds: [orderId]
      });

      const result = response.data;
      
      if (result.results?.success?.length > 0) {
        toast.success('Pedido sincronizado exitosamente', {
          icon: '✅'
        });
        // Refrescar los datos
        handleRefresh();
      } else {
        // Extraer el mensaje de error entre comillas simples
        let specificError = 'Error al sincronizar el pedido';
        
        if (result.results?.failed?.length > 0) {
          const failedOrder = result.results.failed[0];
          if (failedOrder.error) {
            // Buscar el texto entre comillas simples
            const match = failedOrder.error.match(/'([^']+)'/);
            if (match) {
              specificError = match[1];
            }
          }
        }
        
        toast.error(`Error al sincronizar: ${specificError}`, {
          icon: '❌',
          duration: Infinity,
          style: {
            background: '#fef2f2',
            color: '#dc2626',
            border: '1px solid #fecaca',
          }
        });
      }
    } catch (error) {
      console.error('Error sincronizando pedido:', error);
      toast.error('Error al sincronizar el pedido', {
        icon: '❌',
        duration: Infinity,
        style: {
          background: '#fef2f2',
          color: '#dc2626',
          border: '1px solid #fecaca',
        }
      });
    }
  }, [selectedStore, handleRefresh]);

  const handleSyncMissing = useCallback((missingOrders: OrderComparison[]) => {
    console.log('Sincronizar pedidos faltantes:', missingOrders);
  }, []);

  const handleFixDiscrepancies = useCallback((discrepantOrders: OrderComparison[]) => {
    console.log('Corregir discrepancias:', discrepantOrders);
    toast('Funcionalidad de corrección en desarrollo', {
      icon: '🔧'
    });
  }, []);

  // Agregar estilos CSS para el toggle
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .toggle-checkbox:checked {
        right: 0;
        border-color: #3b82f6;
      }
      .toggle-checkbox:checked + .toggle-label {
        background-color: #3b82f6;
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  return (
    <div className="w-full h-full p-4">
      <div className="max-w-full mx-auto">
        {/* Header */}
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-2xl font-semibold text-blue-800">
            Tabla Comparativa ERP
          </h3>
          {/* Acciones de Comparación */}
          {comparisonData.length > 0 && (
            <ComparisonActions
              comparisons={comparisonData}
              selectedStore={selectedStore}
              onRefresh={handleRefresh}
              onSyncMissing={handleSyncMissing}
              onFixDiscrepancies={handleFixDiscrepancies}
            />
          )}
        </div>


        {/* Selector de Tienda y Filtros Combinados */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
          <div className="flex flex-wrap items-end justify-between gap-4">
            {/* Elementos del lado izquierdo */}
            <div className="flex flex-wrap items-end gap-4">
              {/* Selector de Tienda */}
              <div className="flex flex-col">
                <label className="text-sm font-semibold mb-2 whitespace-nowrap">
                  Comparar <span className="text-green-600">SGL</span> con: <span className="text-red-500">*</span>
                </label>
                <select
                  value={selectedStore}
                  onChange={(e) => handleStoreChange(e.target.value)}
                  disabled={isExecuting || isLoading}
                  className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed min-w-[250px]"
                >
                  <option value="">Seleccione</option>
                  <option value="Bigcenter">Bigcenter</option>
                  <option value="Distrito Cafetero">Distrito Cafetero</option>
                  <option value="Artiq">Artiq</option>
                  <option value="Garantias">Garantias</option>
                  <option value="OFIMA">OFIMA</option>
                </select>
              </div>

              {/* Toggle de Filtro por Fecha */}
              <div className="flex flex-col">
                <div className="flex items-center justify-between h-10">
                  <label className="text-sm font-medium text-gray-700 whitespace-nowrap px-2">
                    Filtrar por fecha 
                  </label>
                  <div className="relative inline-block w-10 mr-2 align-middle select-none">
                    <input
                      type="checkbox"
                      id="showFilters"
                      checked={showFilters}
                      onChange={(e) => setShowFilters(e.target.checked)}
                      className="px-2 py-1 toggle-checkbox absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer"
                    />
                    <label className={`toggle-label block overflow-hidden h-6 rounded-full bg-gray-300 cursor-pointer ${showFilters ? 'bg-blue-500' : ''}`}></label>
                  </div>
                </div>
              </div>

              {/* Inputs de Fecha - Solo se muestran si está habilitado */}
              {showFilters && (
                <>
                  <div className="flex flex-col">
                    <label className="text-sm text-gray-600 mb-2 whitespace-nowrap">Desde:</label>
                    <input
                      type="date"
                      value={dateRange.start}
                      onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                      className="border border-gray-300 rounded text-sm px-3 py-2"
                      title="Fecha desde"
                    />
                  </div>
                  <div className="flex flex-col">
                    <label className="text-sm text-gray-600 mb-2 whitespace-nowrap">Hasta:</label>
                    <input
                      type="date"
                      value={dateRange.end}
                      onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                      className="border border-gray-300 rounded text-sm px-3 py-2"
                      title="Fecha hasta"
                    />
                  </div>
                </>
              )}
            </div>

            {/* Botón Comparar - Siempre a la derecha */}
            <div className="flex flex-col">
              <label className="text-sm font-semibold mb-2 invisible">Acción</label>
              <button
                onClick={handleExecuteComparisonClick}
                disabled={!selectedStore || isExecuting || isLoading}
                className="px-4 py-2 bg-blue-800 text-white rounded-md hover:bg-blue-900 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center gap-2 text-sm whitespace-nowrap"
              >
                {isExecuting ? (
                  <>
                    <LoadingSpinner size="small" color="text-white" />
                    <span>Comparando...</span>
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                    <span>Comparar</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
                {/* Métricas de Comparación */}
                <ComparisonMetrics
          metrics={metrics}
          isLoading={isLoading}
          selectedStore={selectedStore}
        />


        {/* Resultados de Comparación */}
        <ComparisonResults
          comparisons={comparisonData}
          isLoading={isLoading}
          onViewDetails={handleViewDetails}
          onSyncOrder={handleSyncOrder}
          selectedStore={selectedStore}
        />

        {/* Paginación */}
        {comparisonData.length > 0 && (
          <Pagination
            currentPage={pagination.currentPage}
            totalPages={pagination.totalPages}
            pageSize={pagination.pageSize}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            searchTerm={searchTerm}
            onSearchChange={handleSearchChange}
            isLoading={isLoading || isExecuting}
          />
        )}

        {/* Error State */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex items-center">
              <svg className="w-5 h-5 text-red-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <div>
                <h3 className="text-sm font-medium text-red-800">Error en la comparación</h3>
                <p className="text-sm text-red-700 mt-1">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Loading Overlay */}
        {(isLoading || isExecuting) && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 flex items-center gap-4">
              <LoadingSpinner size="large" />
              <div>
                <h3 className="text-lg font-medium text-gray-900">
                  {isExecuting ? 'Ejecutando comparación...' : 'Cargando datos...'}
                </h3>
                <p className="text-sm text-gray-600">
                  {isExecuting 
                    ? `Comparando SGL con ${selectedStore}` 
                    : 'Obteniendo información de los sistemas'
                  }
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ComparativeTable;
